import React, { useMemo } from 'react';
import SiteCharts from '../../../components/SiteCharts';
import { erabDropRateCellChartConfig } from '../cellChartConfigs';

/**
 * Props for the ErabDropRateCell component
 */
interface ErabDropRateCellProps {
  /** Callback function to toggle fullscreen mode */
  onFullscreenToggle: () => void;
  /** Current fullscreen state */
  isFullscreen: boolean;
  /** Cell identifier passed to API as cell_name parameter */
  cellName: string;
}

/**
 * ERAB Drop Rate Chart (LTE Cell Level)
 * 
 * Displays E-UTRAN Radio Access Bearer (ERAB) drop rate metrics for individual LTE cells:
 * - ERAB Drop (count)
 * - ERAB Drop Rate (percentage)
 * 
 * This component is a thin wrapper around the SiteCharts template component,
 * configured with cell-level ERAB drop metrics from cellChartConfigs.ts
 * 
 * @component
 * @example
 * ```tsx
 * <ErabDropRateCell
 *   onFullscreenToggle={() => setFullscreen(!fullscreen)}
 *   isFullscreen={false}
 * />
 * ```
 * 
 * @see {@link ../cellChartConfigs.ts} - Chart configuration (erabDropRateCellChartConfig)
 * @see {@link ../../../components/SiteCharts.tsx} - Template component
 * @see API: /api/lte-cell/erab-drop-rate?cell_name={cellName}
 */

const ErabDropRateCell: React.FC<ErabDropRateCellProps> = ({ 
  onFullscreenToggle, 
  isFullscreen,
  cellName
}) => {
  // Memoize additionalParams to prevent infinite re-renders
  const additionalParams = useMemo(() => ({ cell_name: cellName }), [cellName]);

  return (
    <SiteCharts 
      config={erabDropRateCellChartConfig}
      onFullscreenToggle={onFullscreenToggle}
      isFullscreen={isFullscreen}
      additionalParams={additionalParams}
    />
  );
};

export default ErabDropRateCell;
